import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '@/config/api';
import { Loader2, CheckCircle, AlertCircle, Copy, Check } from 'lucide-react';
import { trackCheckoutComplete } from '@/lib/analytics';
import { trySendLicenseToExtension } from '@/lib/extensionBridge';

type SessionStatus = 'loading' | 'success' | 'error';

interface SessionResult {
  status: SessionStatus;
  message?: string;
  planName?: string;
  packageName?: string;
  licenseKey?: string;
}

export function CheckoutCompletePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshLicense, isAuthenticated, user } = useAuth();
  const hasVerified = useRef(false);

  const [result, setResult] = useState<SessionResult>({ status: 'loading' });
  const [copied, setCopied] = useState(false);
  const [extensionSent, setExtensionSent] = useState(false);

  const copyLicenseKey = async () => {
    if (result.licenseKey) {
      await navigator.clipboard.writeText(result.licenseKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    // Prevent double execution
    if (hasVerified.current) return;
    
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setResult({
        status: 'error',
        message: 'No session ID found. Please try again.',
      });
      return;
    }

    const verifySession = async () => {
      hasVerified.current = true;
      
      try {
        const response = await fetch(
          `${API_ENDPOINTS.SUBSCRIBE_SESSION_SUCCESS}?session_id=${encodeURIComponent(sessionId)}`,
          { method: 'GET' }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to verify checkout session');
        }

        // Refresh license to get updated data
        await refreshLicense();
        trackCheckoutComplete(data.plan || data.new_plan || 'unknown', data.license_key);

        setResult({
          status: 'success',
          planName: data.plan || data.new_plan,
          packageName: data.package_name,
          licenseKey: data.license_key,
          message: data.message,
        });

        // If session originated from extension, send the license key
        if (data.license_key) {
          const sent = await trySendLicenseToExtension(
            data.license_key,
            data.plan || data.new_plan || 'free',
          );
          if (sent) setExtensionSent(true);
        }

        // Auto-redirect after 5 seconds (longer to allow user to copy license key)
        // Use window.location.href to force a full page reload so AuthContext re-reads from localStorage
        setTimeout(() => {
          window.location.href = '/account?tab=subscription';
        }, 5000);
      } catch (err) {
        console.error('Session verification error:', err);
        setResult({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to verify checkout',
        });
      }
    };

    verifySession();
  }, [searchParams, refreshLicense, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-[#0A1628] dark:to-[#0A1628]">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center py-20">
            {result.status === 'loading' && (
              <>
                <Loader2 className="w-12 h-12 animate-spin text-[#1E3A8A] mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Processing your payment...
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Please wait while we confirm your subscription.
                </p>
              </>
            )}

            {result.status === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Payment Successful!
                </h2>
                {result.planName && (
                  <p className="text-lg text-gray-700 dark:text-gray-300 mb-2">
                    Welcome to {result.planName}!
                  </p>
                )}
                {result.packageName && (
                  <p className="text-lg text-gray-700 dark:text-gray-300 mb-2">
                    {result.packageName} package has been added.
                  </p>
                )}
                
                {result.licenseKey && (
                  <div className="w-full max-w-md bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Your License Key:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white dark:bg-gray-900 px-3 py-2 rounded text-sm font-mono text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700">
                        {result.licenseKey}
                      </code>
                      <button
                        onClick={copyLicenseKey}
                        className="p-2 bg-[#1E3A8A] text-white rounded hover:bg-[#1E3A8A]/90 transition-colors"
                        title="Copy license key"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {extensionSent
                        ? 'License key has been sent to your xReplAI extension!'
                        : 'Save this key to activate your extension.'}
                    </p>
                  </div>
                )}

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Redirecting to your account in 5 seconds...
                </p>
                <button
                  onClick={() => { window.location.href = '/account?tab=subscription'; }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-colors font-medium"
                >
                  Go to My Account
                </button>
              </>
            )}

            {result.status === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <AlertCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Something went wrong
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                  {result.message}
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={() => navigate('/pricing')}
                    className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                  >
                    Back to Pricing
                  </button>
                  <button
                    onClick={() => navigate('/account')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-colors font-medium"
                  >
                    Go to Account
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
